import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, AlertTriangle, Briefcase, CalendarClock, CheckCircle2, FileSignature,
  Filter, Users, UserCheck, Sparkles, TrendingUp,
} from "lucide-react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip,
  XAxis, YAxis,
} from "recharts";
import { toast } from "sonner";

import { api, fmtDate, fmtTime, relativeTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, KpiCard, PageHeader, StatusBadge, CardSkeleton } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const FUNNEL_COLORS = ["#3b82f6", "#38bdf8", "#22d3ee", "#f59e0b", "#8b5cf6", "#10b981", "#059669"];

const chartTooltip = {
  contentStyle: {
    background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12,
    color: "#0f172a",
  },
  labelStyle: { color: "#475569", fontSize: 11 },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const res = await api.get("/dashboard");
      setData(res.data);
    } catch {
      toast.error("Could not load the dashboard");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const completeTask = async (id) => {
    try {
      await api.patch(`/tasks/${id}/status`, { status: "Completed" });
      toast.success("Task completed");
      load();
    } catch {
      toast.error("Could not update the task");
    }
  };

  if (!data)
    return (
      <div className="space-y-6">
        <Skeleton className="h-16 bg-slate-100" />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-md bg-slate-100" />
          ))}
        </div>
        <CardSkeleton count={3} />
      </div>
    );

  const k = data.kpis;
  const firstName = user?.name?.split(" ")[0];

  return (
    <div className="space-y-8">
      <PageHeader
        meta={new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
        title={`Good to see you, ${firstName}`}
        subtitle="Everything that needs your attention across live requirements, candidates and clients."
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("/analytics")} data-testid="dashboard-analytics-button"
              className="h-9 gap-2 rounded-full border-slate-200 bg-transparent text-xs text-slate-600 hover:bg-slate-100">
              <TrendingUp className="h-3.5 w-3.5" /> Full analytics
            </Button>
            <Button onClick={() => navigate("/ai")} data-testid="dashboard-ai-button"
              className="h-9 gap-2 rounded-full bg-violet-600/90 px-4 text-xs hover:bg-violet-500">
              <Sparkles className="h-3.5 w-3.5" /> AI Intelligence
            </Button>
          </>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Open jobs" value={k.open_jobs} icon={Briefcase} onClick={() => navigate("/jobs?status=Open")} testid="kpi-open-jobs" />
        <KpiCard label="Active candidates" value={k.active_candidates} icon={Users} tone="cyan" onClick={() => navigate("/candidates")} testid="kpi-active-candidates" />
        <KpiCard label="In screening" value={k.in_screening} icon={Filter} tone="violet" testid="kpi-screening" />
        <KpiCard label="Client submissions" value={k.client_submissions} icon={UserCheck} tone="amber" testid="kpi-submissions" />
        <KpiCard label="Interviews this week" value={k.interviews_this_week} icon={CalendarClock} onClick={() => navigate("/interviews")} testid="kpi-interviews" />
        <KpiCard label="Live offers" value={k.offers} icon={FileSignature} tone="emerald" onClick={() => navigate("/offers")} testid="kpi-offers" />
        <KpiCard label="Joining this month" value={k.joining_this_month} icon={CheckCircle2} tone="emerald" testid="kpi-joining" />
        <KpiCard label="Pipeline records" value={data.funnel?.[0]?.count ?? 0} icon={Activity} tone="slate" testid="kpi-pipeline" />
      </div>

      <div className="grid gap-6 xl:grid-cols-12">
        <section className="surface rounded-md p-6 xl:col-span-7">
          <div className="flex items-baseline justify-between">
            <div>
              <p className="label-xs">Recruitment funnel</p>
              <h2 className="mt-1 text-lg font-medium text-slate-900">Stage conversion</h2>
            </div>
            <span className="text-[11px] text-slate-500">Cumulative reach per stage</span>
          </div>
          <div className="mt-6 h-64 min-h-[240px] w-full" data-testid="funnel-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.funnel} layout="vertical" margin={{ left: 8, right: 18 }}>
                <CartesianGrid horizontal={false} stroke="#e2e8f0" />
                <XAxis type="number" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="stage" stroke="#475569" fontSize={11} width={92} tickLine={false} axisLine={false} />
                <Tooltip {...chartTooltip} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={18}>
                  {data.funnel.map((_, i) => (
                    <Cell key={i} fill={FUNNEL_COLORS[i % FUNNEL_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 border-t border-slate-200 pt-5 sm:grid-cols-4">
            {data.funnel.slice(0, 4).map((f, i) => {
              const base = data.funnel[0]?.count || 1;
              return (
                <div key={f.stage}>
                  <p className="label-xs">{f.stage}</p>
                  <p className="font-mono-num mt-1 text-lg text-slate-900">
                    {Math.round((f.count / base) * 100)}%
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="surface rounded-md p-6 xl:col-span-5">
          <p className="label-xs">Six month trend</p>
          <h2 className="mt-1 text-lg font-medium text-slate-900">Applications vs joins</h2>
          <div className="mt-6 h-64 min-h-[240px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.trend}>
                <defs>
                  <linearGradient id="gApps" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gJoin" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip {...chartTooltip} />
                <Area type="monotone" dataKey="applications" stroke="#3b82f6" fill="url(#gApps)" strokeWidth={2} />
                <Area type="monotone" dataKey="joined" stroke="#10b981" fill="url(#gJoin)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>
      </div>

      <div className="grid gap-6 xl:grid-cols-12">
        <section className="surface rounded-md p-6 xl:col-span-4">
          <p className="label-xs">Today</p>
          <h2 className="mt-1 text-lg font-medium text-slate-900">Needs your attention</h2>
          <div className="mt-5 space-y-2">
            {data.highlights.map((h) => (
              <button
                key={h.label}
                onClick={() => navigate(h.link)}
                data-testid="dashboard-highlight"
                className="flex w-full items-center justify-between gap-3 rounded border border-slate-200 bg-slate-50 px-3 py-2.5 text-left text-sm text-slate-600 transition-colors hover:border-blue-300 hover:text-slate-900"
              >
                <span>{h.label}</span>
                <span className="text-[10px] text-slate-400">Open</span>
              </button>
            ))}
          </div>

          {data.tasks?.length > 0 && (
            <>
              <p className="label-xs mt-7">My open tasks</p>
              <ul className="mt-3 space-y-2.5">
                {data.tasks.slice(0, 6).map((t) => (
                  <li key={t.id} className="flex items-start gap-2.5" data-testid="dashboard-task">
                    <button
                      onClick={() => completeTask(t.id)}
                      aria-label={`Complete ${t.title}`}
                      className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-sm border border-slate-300 hover:border-emerald-400"
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-xs text-slate-600">{t.title}</span>
                      <span className="flex items-center gap-2 text-[10px] text-slate-400">
                        {fmtDate(t.due_date)}
                        {t.is_overdue && (
                          <span className="inline-flex items-center gap-1 text-rose-600">
                            <AlertTriangle className="h-2.5 w-2.5" /> overdue
                          </span>
                        )}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        <section className="surface rounded-md p-6 xl:col-span-4">
          <div className="flex items-baseline justify-between">
            <div>
              <p className="label-xs">Schedule</p>
              <h2 className="mt-1 text-lg font-medium text-slate-900">Upcoming interviews</h2>
            </div>
            <button onClick={() => navigate("/interviews")} className="text-[11px] text-blue-600 hover:text-blue-700">
              View all
            </button>
          </div>
          {data.upcoming_interviews.length === 0 ? (
            <EmptyState icon={CalendarClock} title="No interviews lined up" description="Schedule a round from a candidate profile or the pipeline board." />
          ) : (
            <ul className="mt-5 divide-y divide-slate-200">
              {data.upcoming_interviews.map((iv) => (
                <li key={iv.id} className="py-3 first:pt-0" data-testid="dashboard-interview">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm text-slate-800">{iv.candidate_name}</p>
                      <p className="truncate text-xs text-slate-500">
                        {iv.job_title} · {iv.client_name || "Internal"}
                      </p>
                      <p className="mt-1 text-[11px] text-slate-400">
                        {iv.round_name} · {iv.interviewer_name || "Panel to be assigned"}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="font-mono-num text-xs text-slate-600">{fmtTime(iv.scheduled_at)}</p>
                      <p className="text-[10px] text-slate-400">{fmtDate(iv.scheduled_at)}</p>
                      <StatusBadge value={iv.status} className="mt-1.5" />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="surface rounded-md p-6 xl:col-span-4">
          <p className="label-xs">Team feed</p>
          <h2 className="mt-1 text-lg font-medium text-slate-900">Recent activity</h2>
          {data.activity.length === 0 ? (
            <EmptyState icon={Activity} title="No activity yet" description="Actions across jobs and candidates will appear here." />
          ) : (
            <ul className="mt-5 space-y-4">
              {data.activity.slice(0, 9).map((a) => (
                <li key={a.id} className="flex gap-3" data-testid="dashboard-activity">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                  <div className="min-w-0">
                    <p className="text-xs leading-relaxed text-slate-600">
                      <span className="text-slate-900">{a.user_name}</span> {a.action}
                    </p>
                    <p className="mt-0.5 text-[10px] text-slate-400">{relativeTime(a.created_at)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
